/**
 * SaveAsMealPlanModal Component
 * Modal for saving a meal plan as a new copy with different name/profile
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
    mealPlanApi,
    CreateMealPlanData,
    Profile,
    MealPlanData,
} from "../services/api";
import { ProfileCombobox } from "./ProfileCombobox";

interface SaveAsMealPlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaveSuccess?: (savedMealPlan: any) => void;
    currentName: string;
    mealPlanData: MealPlanData;
    originalMealPlan?: {
        generation_prompt?: string;
        generation_url?: string;
        token_count?: number;
    };
}

export const SaveAsMealPlanModal: React.FC<SaveAsMealPlanModalProps> = ({
    isOpen,
    onClose,
    onSaveSuccess,
    currentName,
    mealPlanData,
    originalMealPlan,
}) => {
    const { dbUser, profiles } = useAuth();
    const [mealPlanName, setMealPlanName] = useState("");
    const [selectedProfile, setSelectedProfile] = useState<Profile | null>(
        null
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Set default values when modal opens
    useEffect(() => {
        if (isOpen) {
            setMealPlanName(currentName + " Copy");
            // Find and select the main profile by default
            const mainProfile = profiles.find((p) =>
                p.name.toLowerCase().includes("main")
            );
            setSelectedProfile(mainProfile || profiles[0] || null);
            setError(null);
        }
    }, [isOpen, currentName, profiles]);

    const resetForm = () => {
        setMealPlanName("");
        setSelectedProfile(null);
        setError(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleProfileSelect = (profile: Profile | null) => {
        setSelectedProfile(profile);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!dbUser?.id) {
            setError("User not authenticated");
            return;
        }

        if (!mealPlanName.trim()) {
            setError("Please enter a meal plan name");
            return;
        }

        if (!selectedProfile) {
            setError("Please select a profile");
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Create the meal plan with the correct backend structure
            const createData: CreateMealPlanData = {
                name: mealPlanName.trim(),
                profiles: [selectedProfile.id], // Array of profile IDs
                plan: {
                    ...mealPlanData,
                    // Add metadata to the plan
                    copied_at: new Date().toISOString(),
                    copied_from: currentName,
                },
                // Preserve original generation metadata if available
                generation_prompt: originalMealPlan?.generation_prompt,
                generation_url: originalMealPlan?.generation_url,
                // Set token count to 0 since this is a copy and didn't cost tokens
                token_count: 0,
            };

            const savedMealPlan = await mealPlanApi.createMealPlan(dbUser.id, createData);

            // Success!
            handleClose();
            onSaveSuccess?.(savedMealPlan);
        } catch (err) {
            console.error("Error saving meal plan copy:", err);
            setError(
                err instanceof Error
                    ? err.message
                    : "Failed to save meal plan copy"
            );
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Styles (reusing from ImportMealPlanModal)
    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
    };

    const modalStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "32px",
        maxWidth: "540px",
        width: "90%",
        maxHeight: "90vh",
        overflow: "auto",
        boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    };

    const headerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px",
        paddingBottom: "20px",
        borderBottom: "1px solid #e5e7eb",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "24px",
        fontWeight: "600",
        color: "#1f2937",
        margin: 0,
    };

    const closeButtonStyle: React.CSSProperties = {
        background: "none",
        border: "none",
        fontSize: "24px",
        cursor: "pointer",
        color: "#6b7280",
        padding: "4px",
        borderRadius: "4px",
        width: "32px",
        height: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    const formStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        gap: "24px",
    };

    const fieldStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    };

    const labelStyle: React.CSSProperties = {
        fontSize: "14px",
        fontWeight: "600",
        color: "#374151",
        display: "flex",
        alignItems: "center",
        gap: "4px",
    };

    const requiredStyle: React.CSSProperties = {
        color: "#dc2626",
        fontSize: "14px",
    };

    const inputStyle: React.CSSProperties = {
        padding: "12px 16px",
        border: "1px solid #d1d5db",
        borderRadius: "8px",
        fontSize: "14px",
        outline: "none",
        transition: "all 0.2s",
        backgroundColor: "white",
    };

    const buttonGroupStyle: React.CSSProperties = {
        display: "flex",
        gap: "12px",
        justifyContent: "flex-end",
        marginTop: "8px",
        paddingTop: "24px",
        borderTop: "1px solid #e5e7eb",
    };

    const cancelButtonStyle: React.CSSProperties = {
        padding: "12px 24px",
        border: "1px solid #d1d5db",
        borderRadius: "8px",
        backgroundColor: "white",
        color: "#374151",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s",
    };

    const submitButtonStyle: React.CSSProperties = {
        padding: "12px 24px",
        border: "none",
        borderRadius: "8px",
        backgroundColor: "#10b981",
        color: "white",
        cursor: loading ? "not-allowed" : "pointer",
        fontSize: "14px",
        fontWeight: "500",
        opacity: loading ? 0.6 : 1,
        transition: "all 0.2s",
    };

    const errorStyle: React.CSSProperties = {
        backgroundColor: "#fef2f2",
        border: "1px solid #fecaca",
        color: "#dc2626",
        padding: "12px 16px",
        borderRadius: "8px",
        fontSize: "14px",
        marginBottom: "16px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };

    const infoStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
        marginTop: "6px",
        lineHeight: "1.4",
    };

    return (
        <div style={overlayStyle} onClick={handleClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div style={headerStyle}>
                    <h2 style={titleStyle}>Save As New Meal Plan</h2>
                    <button
                        onClick={handleClose}
                        style={closeButtonStyle}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f3f4f6";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                                "transparent";
                        }}
                    >
                        ×
                    </button>
                </div>

                {error && (
                    <div style={errorStyle}>
                        <span>⚠️</span>
                        <div>
                            <strong>Error:</strong> {error}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={formStyle}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>
                            Meal Plan Name
                            <span style={requiredStyle}>*</span>
                        </label>
                        <input
                            type="text"
                            value={mealPlanName}
                            onChange={(e) => setMealPlanName(e.target.value)}
                            placeholder="Enter a name for this meal plan copy"
                            style={inputStyle}
                            onFocus={(e) => {
                                e.currentTarget.style.borderColor = "#10b981";
                                e.currentTarget.style.boxShadow =
                                    "0 0 0 3px rgba(16, 185, 129, 0.1)";
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.borderColor = "#d1d5db";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                            disabled={loading}
                        />
                        <div style={infoStyle}>
                            This will create a new meal plan with the current
                            content
                        </div>
                    </div>

                    <div style={fieldStyle}>
                        <label style={labelStyle}>
                            Profile
                            <span style={requiredStyle}>*</span>
                        </label>
                        <ProfileCombobox
                            profiles={profiles}
                            selectedProfile={selectedProfile}
                            onProfileSelect={handleProfileSelect}
                            placeholder="Select a profile for this meal plan"
                            disabled={loading}
                        />
                        <div style={infoStyle}>
                            Choose which profile this meal plan copy will be
                            associated with
                        </div>
                    </div>

                    <div style={buttonGroupStyle}>
                        <button
                            type="button"
                            onClick={handleClose}
                            style={cancelButtonStyle}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    "#f9fafb";
                                e.currentTarget.style.borderColor = "#9ca3af";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "white";
                                e.currentTarget.style.borderColor = "#d1d5db";
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={submitButtonStyle}
                            onMouseEnter={(e) => {
                                if (!loading) {
                                    e.currentTarget.style.backgroundColor =
                                        "#059669";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!loading) {
                                    e.currentTarget.style.backgroundColor =
                                        "#10b981";
                                }
                            }}
                            disabled={
                                loading ||
                                !mealPlanName.trim() ||
                                !selectedProfile
                            }
                        >
                            {loading ? "Saving..." : "Save As New Plan"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
